import React, { useEffect, useId, useMemo, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import * as RPopover from '@radix-ui/react-popover';
import { ChevronDown, FileSpreadsheet, FileText, Loader2, Lock, Search } from 'lucide-react';
import { Button, Checkbox, ErrorBlock, Field, Input, Pill, Segmented, Select, Skeleton, Switch, downloadFile, errMsg, money, todayIso, useLoad } from '../kit';
import { API, FigureLine, addDays, plural } from './shared';

// Time and Case Notes (owner spec v1.0 section 5): one row per time entry with its full case note,
// claim and lock state, and the charge; totals per participant and per support coordinator; Excel
// and PDF. Lives in Reports and oversight > Reports, and opens pre-filtered from a participant's
// Notes screen:
//   /support-coordination?view=dashboard&tab=reports&report=time-case-notes&client_ids=<client id>
// (client_ids is the participant's clients.id; several can be comma-separated). Optional from/to
// (YYYY-MM-DD) in the address set the dates.
// Data: /api/sc/insights/time-case-notes (backend/routes/scReports.js, lib/scTimeCaseNotes.js).
// Someone with only Log time sees their own entries; the server decides that, not this screen.

const TCN = `${API}/time-case-notes`;

// DD/MM/YYYY, as the spec asks for dates in this report.
const dmy = (iso) => { const s = String(iso || '').slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : ''; };
const weekday = (iso) => new Date(`${iso}T00:00:00Z`).getUTCDay();
const monthEnd = (y, m) => addDays(`${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}-01`, -1);

// Quick picks (5.2): this week, last week, this month, last month, and the current plan period when
// exactly one participant is chosen.
function quickPicks(today, plan) {
  const mon = addDays(today, -((weekday(today) + 6) % 7));
  const [y, m] = today.split('-').map(Number);
  const py = m === 1 ? y - 1 : y; const pm = m === 1 ? 12 : m - 1;
  const pad = (n) => String(n).padStart(2, '0');
  const list = [
    { key: 'this-week', label: 'This week', from: mon, to: addDays(mon, 6) },
    { key: 'last-week', label: 'Last week', from: addDays(mon, -7), to: addDays(mon, -1) },
    { key: 'this-month', label: 'This month', from: `${y}-${pad(m)}-01`, to: monthEnd(y, m) },
    { key: 'last-month', label: 'Last month', from: `${py}-${pad(pm)}-01`, to: monthEnd(py, pm) },
  ];
  if (plan && plan.plan_start && plan.plan_end) list.push({ key: 'plan', label: 'Current plan period', from: plan.plan_start, to: plan.plan_end });
  return list;
}

const CLAIM_OPTIONS = [{ value: 'all', label: 'All' }, { value: 'not_claimed', label: 'Not claimed' }, { value: 'claimed', label: 'Claimed' }];
const BILLABLE_OPTIONS = [{ value: 'all', label: 'All' }, { value: 'billable', label: 'Billable' }, { value: 'non_billable', label: 'Non-billable' }];
const readIds = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

// A file download's error arrives as a Blob when the request asked for one; read the message out.
async function downloadError(e, fallback) {
  const d = e && e.response && e.response.data;
  if (d && typeof Blob !== 'undefined' && d instanceof Blob) {
    try { const j = JSON.parse(await d.text()); if (j && j.message) return j.message; } catch { /* not JSON */ }
  }
  return errMsg(e, fallback);
}

export default function TimeCaseNotesReport({ initialClientIds, initialFrom, initialTo, header }) {
  const opts = useLoad(`${TCN}/options`);
  const today = useMemo(() => todayIso(), []);
  const [clientIds, setClientIds] = useState(() => readIds(Array.isArray(initialClientIds) ? initialClientIds.join(',') : initialClientIds));
  const [coordinatorIds, setCoordinatorIds] = useState([]);
  const [activityIds, setActivityIds] = useState([]);
  const [claimStatus, setClaimStatus] = useState('all');
  const [billable, setBillable] = useState('all');
  const [showCharges, setShowCharges] = useState(true);
  const [includeHistory, setIncludeHistory] = useState(false);
  const [period, setPeriod] = useState(() => (isIso(initialFrom) && isIso(initialTo) && initialFrom <= initialTo
    ? { key: 'custom', from: initialFrom, to: initialTo } : { key: 'this-month', ...quickPicks(today)[2] }));
  const [busy, setBusy] = useState(null);

  const data = opts.data;
  const participants = useMemo(() => (data && data.participants) || [], [data]);
  const onePlan = clientIds.length === 1 ? participants.find((p) => p.client_id === clientIds[0]) : null;
  const picks = useMemo(() => quickPicks(today, onePlan), [today, onePlan]);
  // The plan period quick pick goes away when the selection is no longer one participant.
  useEffect(() => {
    if (period.key === 'plan' && !picks.some((p) => p.key === 'plan')) setPeriod((p) => ({ ...p, key: 'custom' }));
  }, [period.key, picks]);

  const rangeBad = !period.from || !period.to || period.from > period.to;
  const params = useMemo(() => ({
    from: period.from, to: period.to,
    ...(clientIds.length ? { client_ids: clientIds.join(',') } : {}),
    ...(coordinatorIds.length ? { coordinator_ids: coordinatorIds.join(',') } : {}),
    ...(activityIds.length ? { activity_type_ids: activityIds.join(',') } : {}),
    claim_status: claimStatus, billable, show_charges: showCharges ? 1 : 0, include_history: includeHistory ? 1 : 0,
  }), [period.from, period.to, clientIds, coordinatorIds, activityIds, claimStatus, billable, showCharges, includeHistory]);
  const preview = useLoad(`${TCN}/preview`, params, { skip: rangeBad || !data });

  const download = async (format) => {
    setBusy(format);
    try {
      const name = await downloadFile(TCN, { params: { ...params, format } });
      toast.success(`Downloaded ${name}`);
    } catch (e) { toast.error(await downloadError(e, 'Couldn’t make the file. Please try again.')); }
    finally { setBusy(null); }
  };

  const pickPeriod = (key) => {
    if (key === 'custom') { setPeriod((p) => ({ ...p, key: 'custom' })); return; }
    const p = picks.find((x) => x.key === key);
    if (p) setPeriod({ key: p.key, from: p.from, to: p.to });
  };

  if (opts.error && !data) return <ErrorBlock error={opts.error} onRetry={opts.reload} />;
  const own = data && data.scope === 'own';
  const rep = preview.data;

  return (
    <section aria-labelledby="tcn-h" className="bg-white rounded-2xl border border-slate-200/80">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 sm:px-5 pt-4 pb-3">
        <div className="min-w-0">
          {header || <h2 id="tcn-h" className="text-[16px] font-semibold text-slate-900 leading-6">Time and Case Notes</h2>}
          <p className="text-[13px] text-slate-600 mt-0.5 max-w-xl">
            Every time entry with its full case note, claim and lock status, by participant and support coordinator. Downloading never locks anything.
            {own && ' You can see your own entries.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" icon={FileSpreadsheet} onClick={() => download('xlsx')} loading={busy === 'xlsx'} disabled={!!busy || rangeBad || !data}>Download Excel</Button>
          <Button variant="secondary" icon={FileText} onClick={() => download('pdf')} loading={busy === 'pdf'} disabled={!!busy || rangeBad || !data}>Download PDF</Button>
        </div>
      </div>

      {/* Filters (5.2) */}
      <div className="px-4 sm:px-5 pb-4 border-b border-slate-100">
        {!data ? <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" aria-busy="true" aria-label="Loading">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}</div> : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              <Field label="Participant">
                <MultiPick value={clientIds} onChange={setClientIds} noun={['participant', 'participants']} allLabel="All participants"
                  options={participants.map((p) => ({ value: p.client_id, label: p.name, hint: p.ndis_number ? `NDIS ${p.ndis_number}` : '' }))} />
              </Field>
              <Field label="Support coordinator">
                <MultiPick value={coordinatorIds} onChange={setCoordinatorIds} noun={['coordinator', 'coordinators']}
                  allLabel={own ? 'Your entries' : 'All coordinators'} disabled={own}
                  options={(data.coordinators || []).map((c) => ({ value: c.id, label: c.name }))} />
              </Field>
              <Field label="Activity type">
                <MultiPick value={activityIds} onChange={setActivityIds} noun={['activity type', 'activity types']} allLabel="All activity types"
                  options={(data.activity_types || []).map((a) => ({ value: a.id, label: a.name, hint: a.is_active === false ? 'No longer used' : '' }))} />
              </Field>
            </div>
            <div className="flex flex-wrap items-start gap-3">
              <Field label="Date range" className="w-full sm:w-60">
                <Select value={period.key} onChange={pickPeriod}
                  options={[...picks.map((p) => ({ value: p.key, label: p.label, hint: `${dmy(p.from)} to ${dmy(p.to)}` })), { value: 'custom', label: 'Custom dates' }]} />
              </Field>
              <Field label="From" className="w-[calc(50%-0.375rem)] sm:w-40" error={period.from && period.to && period.from > period.to ? 'Pick a From date on or before the To date.' : undefined}>
                <Input type="date" value={period.from || ''} onChange={(e) => setPeriod((p) => ({ ...p, key: 'custom', from: e.target.value }))} />
              </Field>
              <Field label="To" className="w-[calc(50%-0.375rem)] sm:w-40">
                <Input type="date" value={period.to || ''} onChange={(e) => setPeriod((p) => ({ ...p, key: 'custom', to: e.target.value }))} />
              </Field>
              <Field label="Claim status" className="w-full sm:w-auto">
                <Segmented value={claimStatus} onChange={setClaimStatus} options={CLAIM_OPTIONS} size="sm" />
              </Field>
              <Field label="Billable" className="w-full sm:w-auto">
                <Segmented value={billable} onChange={setBillable} options={BILLABLE_OPTIONS} size="sm" />
              </Field>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <div className="w-full sm:w-64"><Switch checked={showCharges} onChange={setShowCharges} label="Show charges" hint="Unit price and amount as claimed." /></div>
              <div className="w-full sm:w-64"><Switch checked={includeHistory} onChange={setIncludeHistory} label="Include note history" hint="Earlier versions of edited notes." /></div>
            </div>
            {!rangeBad && <p className="text-[12.5px] text-slate-500">Dates {dmy(period.from)} to {dmy(period.to)}</p>}
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="px-4 sm:px-5 py-4">
        {rangeBad ? <p className="text-[13px] text-slate-600 py-6 text-center">Pick a From and a To date to see the report.</p>
          : preview.error && !rep ? <ErrorBlock error={preview.error} onRetry={preview.reload} />
            : !rep ? <div className="space-y-2" aria-busy="true" aria-label="Loading">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8" />)}</div>
              : (
                <>
                  <p className="text-[13px] text-slate-600 mb-3 flex items-center gap-2" aria-live="polite">
                    {rep.count === 0 ? 'No entries' : rep.count > rep.rows.length ? `First ${rep.rows.length} of ${rep.count} entries. The download has them all.` : plural(rep.count, 'entry', 'entries')}
                    {preview.loading && <Loader2 size={13} className="animate-spin text-slate-500" aria-label="Updating" />}
                  </p>
                  {rep.count === 0
                    ? <p className="text-[13px] text-slate-600 py-6 text-center">No time entries match these filters. Try a longer date range.</p>
                    : (
                      <>
                        <EntriesTable rows={rep.rows} showCharges={!!rep.filters.show_charges} />
                        <Totals totals={rep.totals} showCharges={!!rep.filters.show_charges} />
                      </>
                    )}
                </>
              )}
      </div>
    </section>
  );
}

function EntriesTable({ rows, showCharges }) {
  const cols = ['Participant', 'Date', 'Start and end', 'Duration', 'Support coordinator', 'Activity type', 'Billable', 'Claim status', 'Locked', 'Case note', 'Edited after claim'];
  if (showCharges) cols.push('Charge');
  const td = 'px-3 py-2 border-b border-slate-100 text-slate-700 align-top';
  return (
    // Scrolls inside its panel on narrow screens; the page never scrolls sideways.
    <div className="overflow-x-auto -mx-1 px-1 pb-1">
      <table className="w-full text-[13px] border-separate border-spacing-0">
        <caption className="sr-only">Time and Case Notes: entries</caption>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c} scope="col" className={clsx('bg-slate-50/95 text-[12px] font-semibold text-slate-600 border-y border-slate-200/80 px-3 py-2 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap text-left',
                (c === 'Duration' || c === 'Charge') && 'text-right')}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <React.Fragment key={r.id}>
              <tr className={clsx(r.kind === 'adjustment' && 'bg-violet-50/40')}>
                <td className={clsx(td, 'min-w-[10rem]', r.kind === 'adjustment' && 'pl-7')}>
                  <span className="block font-medium text-slate-900">{r.participant}</span>
                  {r.ndis_number && <span className="block text-[12px] text-slate-500">NDIS {r.ndis_number}</span>}
                </td>
                <td className={clsx(td, 'whitespace-nowrap tabular-nums')}>{r.date}</td>
                <td className={clsx(td, 'whitespace-nowrap tabular-nums')}>{r.start || r.end ? `${r.start || '?'} to ${r.end || '?'}` : '—'}</td>
                <td className={clsx(td, 'text-right whitespace-nowrap tabular-nums')}>
                  <span className="block">{r.minutes} min</span>
                  <span className="block text-[12px] text-slate-500">{Number(r.hours).toFixed(2)} h</span>
                </td>
                <td className={clsx(td, 'whitespace-nowrap')}>{r.coordinator}</td>
                <td className={td}>{r.activity}</td>
                <td className={td}>{r.billable ? 'Yes' : 'No'}</td>
                <td className={clsx(td, 'min-w-[8rem]')}>
                  {r.claim_status === 'Claimed' ? (
                    <>
                      <Pill tone="blue" label="Claimed" />
                      {r.claim_ref && <span className="block text-[12px] text-slate-600 mt-1">{r.claim_ref}</span>}
                      {r.claim_downloaded && <span className="block text-[12px] text-slate-500">Downloaded {r.claim_downloaded}</span>}
                    </>
                  ) : <span className="text-slate-500">Not claimed</span>}
                </td>
                <td className={clsx(td, 'whitespace-nowrap')}>
                  {r.locked ? <span className="inline-flex items-center gap-1 text-violet-700"><Lock size={13} aria-hidden />Yes</span> : 'No'}
                </td>
                <td className={clsx(td, 'min-w-[16rem] max-w-[32rem]')}>
                  {r.kind === 'adjustment' && <Pill tone="violet" label="Adjustment" className="mb-1" />}
                  {/* The full note, never cut off (5.3). */}
                  <span className="block whitespace-pre-wrap break-words">{r.note || '—'}</span>
                  {r.kind === 'adjustment' && r.adjustment_reason && !String(r.note || '').includes(r.adjustment_reason) && (
                    <span className="block text-[12px] text-slate-600 mt-1">Reason: {r.adjustment_reason}</span>
                  )}
                </td>
                <td className={clsx(td, 'whitespace-nowrap text-[12.5px]')}>{r.edited_after_claim || ''}</td>
                {showCharges && (
                  <td className={clsx(td, 'text-right whitespace-nowrap tabular-nums')}>
                    <span className="block">{r.amount != null ? money(r.amount) : '—'}</span>
                    {r.unit_price != null && <span className="block text-[12px] text-slate-500">{money(r.unit_price)}/h</span>}
                  </td>
                )}
              </tr>
              {(r.history || []).map((v) => (
                <tr key={`${r.id}-v${v.version_no}`}>
                  <td className={clsx(td, 'text-[12px] text-slate-500')} colSpan={9}>
                    <span className="pl-4">Earlier note, version {v.version_no}</span>
                    <span className="block pl-4">{v.edited_at}{v.edited_by ? ` by ${v.edited_by}` : ''}</span>
                  </td>
                  <td className={clsx(td, 'text-[12.5px] text-slate-500')} colSpan={showCharges ? 3 : 2}>
                    <span className="block whitespace-pre-wrap break-words">{v.summary}</span>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const figures = (t, showCharges) => [
  { label: 'Minutes', value: t.minutes },
  { label: 'Hours', value: Number(t.hours).toFixed(2) },
  { label: 'Billable', value: `${t.billable_minutes} min (${Number(t.billable_hours).toFixed(2)} h)` },
  { label: 'Non-billable', value: `${t.non_billable_minutes} min (${Number(t.non_billable_hours).toFixed(2)} h)` },
  showCharges && { label: 'Charges', value: money(t.charges) },
  showCharges && { label: 'Billable charges', value: money(t.billable_charges) },
  showCharges && { label: 'Non-billable charges', value: money(t.non_billable_charges) },
];

// Totals (5.4): per participant, per coordinator within it, and the grand total.
function Totals({ totals, showCharges }) {
  return (
    <div className="mt-5 space-y-3">
      <h3 className="text-[13.5px] font-semibold text-violet-800">Totals</h3>
      {totals.participants.map((p) => (
        <div key={p.client_id || 'none'} className="rounded-xl ring-1 ring-inset ring-slate-200/80 px-4 py-3">
          <p className="text-[13.5px] font-semibold text-slate-900">{p.participant}{p.ndis_number && <span className="font-normal text-slate-500"> · NDIS {p.ndis_number}</span>}</p>
          <FigureLine className="mt-1.5" items={figures(p, showCharges)} />
          {p.coordinators.length > 0 && (
            <ul className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
              {p.coordinators.map((c) => (
                <li key={c.coordinator_id || 'none'} className="text-[12.5px]">
                  <span className="font-medium text-slate-700">{c.coordinator}</span>
                  <FigureLine className="mt-0.5 !text-[12.5px]" items={figures(c, showCharges)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      <div className="rounded-xl bg-violet-50/60 ring-1 ring-inset ring-violet-200/70 px-4 py-3">
        <p className="text-[13.5px] font-semibold text-violet-900">Grand total · {plural(totals.participants.length, 'participant', 'participants')}</p>
        <FigureLine className="mt-1.5" items={figures(totals.grand, showCharges)} />
      </div>
    </div>
  );
}

// One, several or all (5.2): a searchable checkbox list in a popover. An empty value means all.
// options: [{ value, label, hint }].
function MultiPick({ id, value, onChange, options, noun, allLabel, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const uid = useId().replace(/:/g, '');
  const chosen = new Set(value);
  const shown = options.filter((o) => !q.trim() || `${o.label} ${o.hint || ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  const label = !value.length ? allLabel
    : value.length === 1 ? ((options.find((o) => o.value === value[0]) || {}).label || `1 ${noun[0]}`)
      : `${value.length} ${noun[1]}`;
  const toggle = (v, on) => onChange(on ? [...value, v] : value.filter((x) => x !== v));
  return (
    <RPopover.Root open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
      <RPopover.Trigger asChild disabled={disabled}>
        <button id={id} type="button" disabled={disabled}
          className="w-full h-11 sm:h-10 rounded-xl bg-slate-50 ring-1 ring-inset ring-slate-200 px-3 text-[13.5px] text-left text-slate-900 flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white disabled:opacity-60">
          <span className="truncate">{label}</span>
          <ChevronDown size={15} className="text-slate-400 flex-shrink-0" aria-hidden />
        </button>
      </RPopover.Trigger>
      <RPopover.Portal>
        <RPopover.Content align="start" sideOffset={4}
          className="z-[90] w-[var(--radix-popover-trigger-width)] min-w-[16rem] bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-2">
          {options.length > 8 && (
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" aria-label={`Search ${noun[1]}`}
                className="w-full h-9 pl-8 pr-2 rounded-lg bg-slate-50 ring-1 ring-inset ring-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          )}
          <div className="flex items-center justify-between px-1 pb-1.5 text-[12px]">
            <span className="text-slate-500">{value.length ? `${value.length} chosen` : allLabel}</span>
            {value.length > 0 && <button type="button" onClick={() => onChange([])} className="font-semibold text-indigo-700 hover:text-indigo-900">Show all</button>}
          </div>
          <ul className="max-h-64 overflow-y-auto" aria-label={noun[1]} id={`${uid}-list`}>
            {shown.length === 0 && <li className="px-2 py-3 text-[12.5px] text-slate-500">No {noun[1]} match.</li>}
            {shown.map((o) => (
              <li key={o.value} className="px-1.5 py-1.5 rounded-lg hover:bg-slate-50">
                <Checkbox checked={chosen.has(o.value)} onChange={(on) => toggle(o.value, on)}
                  label={<span className="min-w-0"><span className="block">{o.label}</span>{o.hint && <span className="block text-[12px] text-slate-500">{o.hint}</span>}</span>} />
              </li>
            ))}
          </ul>
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}
